import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import useReportDescription from './useReportDescription';
import useUiMode from '../theme/useUiMode';
import { UI_NEW } from '../theme/uiMode';

// The banner, info badge and description card in the new UI (the classic values are
// inline below).
const NEW_UI_BANNER = {
    background: '#201e1d',
    border: '2px solid #201e1d',
    borderRadius: 16,
    boxShadow: '4px 4px 0 #71ae12',
};
const NEW_UI_TITLE = {
    color: '#fff9ec',
    fontFamily: "'Bricolage Grotesque', 'Inter', sans-serif",
    fontWeight: 800,
    letterSpacing: '-0.01em',
};
const NEW_UI_BADGE = { background: '#c9f27a', color: '#201e1d' };
const NEW_UI_CARD = {
    color: '#201e1d',
    border: '2px solid #201e1d',
    borderRadius: 14,
    boxShadow: '4px 4px 0 #201e1d',
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
};
const NEW_UI_CARD_ARROW = { borderLeft: '2px solid #201e1d', borderTop: '2px solid #201e1d', top: -7 };
const NEW_UI_ACCENT = { color: '#3f6b0a' };
const NEW_UI_TAG = { color: '#2b4a06', background: '#e6f3cf' };

/**
 * ReportTitleBanner
 *
 * Drop-in replacement for the shared gradient title banner used on every
 * report page:
 *   <div style={{ background: 'linear-gradient(...)', ... }}>
 *     <h4 style={{ ... }}>TITLE</h4>
 *   </div>
 *
 * Renders identical markup/styling. Additionally looks up the report's
 * Description in the ReportMaster collection (by RouteName) and, only when
 * a Description exists, shows a small info badge on the right side of the
 * banner. Hovering that badge (not the whole title) opens a card anchored
 * to the badge on the right side, clamped so it never overflows the
 * viewport. If there is no Description, the banner behaves exactly like the
 * plain static header (no badge, no hover, no cursor change).
 *
 * Props:
 *   title      - the exact title text currently rendered in <h4>
 *   routeName  - this report's RouteName value in ReportMaster (e.g. 'availability')
 *   displayName (optional) - fallback match key if RouteName doesn't match
 */
export default function ReportTitleBanner({ title, routeName, displayName }) {
    const { description, metaTags } = useReportDescription(routeName, displayName);
    const isNewUi = useUiMode() === UI_NEW;
    const [hovered, setHovered] = useState(false);
    const hasDescription = Boolean(description && description.trim());
    const badgeRef = useRef(null);
    const closeTimer = useRef(null);
    const [pos, setPos] = useState({ top: 0, right: 0 });

    const openPopup = () => {
        if (closeTimer.current) clearTimeout(closeTimer.current);
        const el = badgeRef.current;
        if (el) {
            const r = el.getBoundingClientRect();
            setPos({ top: r.bottom + 10, right: Math.max(8, window.innerWidth - r.right) });
        }
        setHovered(true);
    };
    const closePopup = () => {
        if (closeTimer.current) clearTimeout(closeTimer.current);
        closeTimer.current = setTimeout(() => setHovered(false), 150);
    };

    return (
        <div
            style={{
                background: 'linear-gradient(135deg, #1e3a5f 0%, #2d6aa0 50%, #4a90d9 100%)',
                borderRadius: 12,
                padding: '18px 28px',
                marginBottom: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 15px rgba(30,58,95,0.3)',
                position: 'relative',
                ...(isNewUi ? NEW_UI_BANNER : null),
            }}
        >
            <h4 style={{ color: '#fff', margin: 0, fontSize: 22, fontWeight: 700, letterSpacing: 0.5, ...(isNewUi ? NEW_UI_TITLE : null) }}>
                {title}
            </h4>

            {hasDescription && (
                <span
                    ref={badgeRef}
                    onMouseEnter={openPopup}
                    onMouseLeave={closePopup}
                    style={{
                        position: 'absolute',
                        right: 16,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        background: 'rgba(255,255,255,0.18)',
                        color: '#fff',
                        fontSize: 12,
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        lineHeight: 1,
                        cursor: 'pointer',
                        ...(isNewUi ? NEW_UI_BADGE : null),
                    }}
                >
                    i
                </span>
            )}
            {hasDescription && hovered && createPortal(
                <div
                    onMouseEnter={openPopup}
                    onMouseLeave={closePopup}
                    style={{
                        position: 'fixed',
                        top: pos.top,
                        right: pos.right,
                        width: 'min(360px, calc(100vw - 32px))',
                        maxHeight: '60vh',
                        overflowY: 'auto',
                        background: '#fff',
                        color: '#1e293b',
                        borderRadius: 10,
                        boxShadow: '0 10px 30px rgba(15,32,39,0.22)',
                        border: '1px solid #e0e6ed',
                        padding: '14px 18px',
                        zIndex: 9990,
                        textAlign: 'left',
                        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                        cursor: 'default',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        ...(isNewUi ? NEW_UI_CARD : null),
                    }}
                >
                    <div
                        style={{
                            position: 'absolute',
                            top: -6,
                            right: 6,
                            width: 12,
                            height: 12,
                            transform: 'rotate(45deg)',
                            background: '#fff',
                            borderLeft: '1px solid #e0e6ed',
                            borderTop: '1px solid #e0e6ed',
                            ...(isNewUi ? NEW_UI_CARD_ARROW : null),
                        }}
                    />
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#2d6aa0', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6, ...(isNewUi ? NEW_UI_ACCENT : null) }}>
                        About this report
                    </div>
                    <div style={{ fontSize: 13, lineHeight: 1.5, color: '#334155' }}>{description}</div>
                    {Array.isArray(metaTags) && metaTags.length > 0 && (
                        <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            {metaTags.map((tag, idx) => (
                                <span
                                    key={idx}
                                    style={{
                                        fontSize: 11,
                                        fontWeight: 600,
                                        color: '#2d6aa0',
                                        background: '#eef2ff',
                                        borderRadius: 12,
                                        padding: '3px 10px',
                                        ...(isNewUi ? NEW_UI_TAG : null),
                                    }}
                                >
                                    {tag}
                                </span>
                            ))}
                        </div>
                    )}
                </div>,
                document.body
            )}
        </div>
    );
}
